import {
    useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Badge } from 'reactstrap';
import cls from 'classnames';
import {
    Map, AdvancedMarker, Polyline, useMap,
} from '@vis.gl/react-google-maps';
import { isEmpty } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { faMapMarkerAlt, faTimes, faTruck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Hide, useIsMobile } from '@s20/components/common/Hide';
import { httpClient } from '@s20/services/httpClient';
import { getObjectCoordinates, MAX_ZOOM, useClusters } from '../../../common/Map/useClusters';
import { FormContext, IconButton } from '../../../common/forms/FormElements';
import { ApiRoutes } from '../../../Common';
import { getJobId } from '../common';
import { ActionSheet } from '../../../common/ActionSheet';
import { useJobQuery } from './useJobQuery';
import { useCrewEta } from './useCrewEta';
import { InfoPane } from './InfoPane';
import { EtaDisplay } from './EtaDisplay';

const DEFAULT_ZOOM = 6;
const SELECTED_ZOOM = 13;

const DEFAULT_US_CENTER = { lat: 39.8283, lng: -98.5795 };

export const MapView = ({ mapId, defaultTimesheetId }) => {
    const { values, setFieldValue } = useContext(FormContext);
    const { jobDate, selectedJobId } = values;

    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(defaultTimesheetId ? SELECTED_ZOOM : DEFAULT_ZOOM);

    // On a phone the job sheet covers the map, so the crew lead truck, route and ETA stay on the
    // map after the sheet is dismissed (until another job is picked or tracking is stopped). The
    // desktop side pane leaves the map visible, so closing it clears the tracking as well.
    const isMobile = useIsMobile();
    const [trackedJobId, setTrackedJobId] = useState(null);
    useEffect(() => {
        if (selectedJobId) {
            setTrackedJobId(selectedJobId);
        } else if (!isMobile) {
            setTrackedJobId(null);
        }
    }, [selectedJobId, isMobile]);
    const activeJobId = selectedJobId || trackedJobId;

    const { data: jobs } = useJobQuery();
    const map = useMap();

    useEffect(() => {
        if (!isEmpty(jobs) && map) {
            const [firstJob] = jobs;
            const coordinates = getObjectCoordinates(firstJob);

            if (coordinates) {
                map.panTo(coordinates);
            }
        }
    }, [jobs, map]);

    const selectedJobTimesheetId = useMemo(() => {
        if (!activeJobId) {
            return null;
        }

        const selectedJob = (jobs || []).find((job) => activeJobId === getJobId(job));

        // Don't load vehicle data for completed jobs
        if (selectedJob?.isComplete) {
            return null;
        }

        return selectedJob?.timesheetId;
    }, [jobs, activeJobId]);

    const { data: equipment } = useQuery({
        queryKey: ['equipmentForTimesheet', jobDate, selectedJobTimesheetId],
        queryFn: () => jobDate && selectedJobTimesheetId && httpClient.js(ApiRoutes.jobRequests.equipmentForTimesheet(moment(jobDate).format('YYYY-MM-DD'), selectedJobTimesheetId)),
    });

    // Uber-style route + ETA from the crew lead vehicle to the job site. Live position/ETA arrive
    // over SignalR while the job is open (jobDate lets the hook move the truck marker in the
    // equipment layer); the ETA counts down between pushes.
    const crewEta = useCrewEta(selectedJobTimesheetId, jobDate);
    const trackedJob = useMemo(
        () => (jobs || []).find((job) => trackedJobId === getJobId(job)),
        [jobs, trackedJobId],
    );

    const clusterSelector = useCallback((item) => {
        if (!selectedJobId) {
            return true;
        }

        return selectedJobId === getJobId(item);
    }, [selectedJobId]);

    const [clusters, supercluster] = useClusters(jobs, bounds, zoom, clusterSelector);

    const handleClusterClick = useCallback((latLng, clusterId) => {
        const expansionZoom = Math.min(
            supercluster.getClusterExpansionZoom(clusterId),
            MAX_ZOOM,
        );

        map.setZoom(expansionZoom);
        map.setCenter(latLng);
    }, [supercluster, map]);

    return (
        <>
            <div className="d-flex flex-row flex-fill h-100 position-relative">
                <Map
                    mapId={mapId}
                    defaultZoom={zoom}
                    defaultCenter={DEFAULT_US_CENTER}
                    gestureHandling="greedy"
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    zoomControl={false}
                    scaleControl={false}
                    clickableIcons={false}
                    cameraControl={false}
                    onBoundsChanged={(e) => {
                        setBounds(e.detail.bounds);
                    }}
                    onZoomChanged={(e) => {
                        setZoom(e.detail.zoom);
                    }}
                >
                    {clusters.map((cluster) => {
                        const [lng, lat] = cluster.geometry.coordinates;
                        const {
                            cluster: isCluster,
                            sum,
                        } = cluster.properties;

                        if (isCluster) {
                            return (
                                <AdvancedMarker
                                    key={`cluster-${cluster.id}`}
                                    position={{ lat, lng }}
                                    title={`${sum} jobs at this location`}
                                    className="cluster-marker"
                                >
                                    <IconButton
                                        onClick={() => handleClusterClick({ lat, lng }, cluster.id)}
                                    >
                                        {sum}
                                    </IconButton>
                                </AdvancedMarker>
                            );
                        }

                        const { jobs, objectID: key } = cluster.properties;

                        if (jobs?.length > 1) {
                            return (
                                <AdvancedMarker
                                    key={`cluster-jobs-${key}`}
                                    position={{ lat, lng }}
                                    title={`${jobs.length} jobs at this location`}
                                    className="marker-group"
                                >
                                    <div>
                                        <FontAwesomeIcon
                                            icon={faMapMarkerAlt}
                                            size="2x"
                                            className={cls('map-marker', 'text-danger')}
                                        />

                                        <Badge
                                            color="danger"
                                            className="shadow border"
                                            pill
                                        >
                                            {jobs.length}
                                        </Badge>
                                    </div>
                                </AdvancedMarker>
                            );
                        }

                        const [job] = jobs;

                        return (
                            <AdvancedMarker
                                key={`job-${key}`}
                                position={{ lat, lng }}
                                title={`${job.customerCompanyName || ''} - ${job.jobNumber || 'Request'}`}
                                onClick={() => {
                                    setFieldValue('selectedJobId', getJobId(job));
                                    setFieldValue('selectedEquipmentId', null);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    size="2x"
                                    className={cls('map-marker', `text-${job.jobRequestStatusDisplayColor}`, selectedJobId && selectedJobId === getJobId(job) && 'map-marker-selected')}
                                />
                            </AdvancedMarker>
                        );
                    })}

                    {(equipment || []).map((vehicle) => (
                        <AdvancedMarker
                            key={`job-${vehicle.equipmentId}`}
                            position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
                            onClick={() => {
                                setFieldValue('selectedEquipmentId', vehicle.equipmentId);
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faTruck}
                                size="2x"
                                className={cls('map-marker text-light')}
                            />
                        </AdvancedMarker>
                    ))}

                    {Boolean(crewEta.encodedPolyline) && (
                        <Polyline
                            encodedPath={crewEta.encodedPolyline}
                            strokeColor="#4285F4"
                            strokeOpacity={0.85}
                            strokeWeight={4}
                        />
                    )}
                </Map>
                {isMobile && !selectedJobId && Boolean(trackedJobId) && (
                    <div
                        className="d-flex align-items-start position-absolute w-100 px-3"
                        style={{
                            left: 0, bottom: 16, zIndex: 5, pointerEvents: 'none',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            className="flex-fill bg-white rounded shadow"
                            style={{ pointerEvents: 'auto' }}
                            title="Open job"
                            onClick={() => setFieldValue('selectedJobId', trackedJobId)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setFieldValue('selectedJobId', trackedJobId);
                                }
                            }}
                        >
                            {crewEta.estimatedArrivalTime ? (
                                <EtaDisplay
                                    {...crewEta}
                                    startTime={trackedJob?.startTime}
                                    timeZone={trackedJob?.timeZone}
                                />
                            ) : (
                                <div className="p-2 font-weight-bold">
                                    {`Tracking ${trackedJob?.jobNumber || 'job'}`}
                                </div>
                            )}
                        </div>
                        <div
                            className="ml-2 bg-white rounded shadow"
                            style={{ pointerEvents: 'auto' }}
                        >
                            <IconButton
                                title="Stop tracking"
                                onClick={() => setTrackedJobId(null)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </IconButton>
                        </div>
                    </div>
                )}
            </div>

            <Hide mdUp>
                <div className="flex-none">
                    <InfoPane crewEta={crewEta} />
                </div>
            </Hide>

            <ActionSheet
                isOpen={!!selectedJobId}
                onClose={() => setFieldValue('selectedJobId', null)}
            >
                <InfoPane crewEta={crewEta} />
            </ActionSheet>
        </>
    );
};
